import React, { useEffect, useState } from "react";
import { isBoolean, isObjectNull } from "../../../../../helpers/ObjectHelpers";
import { ControlPosition, MapControl } from "@vis.gl/react-google-maps";
import { Clock, X } from "phosphor-react-sc";
import parse from 'html-react-parser';
import MapPropertiesService from "../../../services/MapPropertiesService";
import Moment from 'moment';
import { isLocationAtSea } from "../../../../../../pages/pilot/pilot-assignment/helpers/PilotAssignmentHelpers";
import { calculateDistance } from "../../../../../helpers/GeometryHelpers";
import { IMapParamsService } from "../../../services/MapParamsService";
import { useSearchParams } from "react-router";
import { getBooleanSearchParam } from "../../../../../helpers/UrlHelpers";
import { AppStore } from 'components/stores/AppStore';
import { useStoreSelector } from 'components/hooks/useStoreSelector';
import { useShowLegend, useSelectShipPosition, useSetShowLegend } from "../../../hooks/useMapStore";

interface MapControlLegendProps {
    pilotage?: any,
    pecExam?: any,
    paramsService: IMapParamsService,
}

export const MapControlLegend = ({
    pilotage,
    pecExam,
    paramsService,
}: MapControlLegendProps) => {

    const stateShowLegend = useShowLegend();
    const stateSetShowLegend = useSetShowLegend();
    const stateSelectShipPosition = useSelectShipPosition();

    const [searchParams, _] = useSearchParams();
    const [showLegend, setShowLegend] = useState(getBooleanSearchParam(searchParams, "sleg"));
    const [shipPosition, setShipPosition] = useState<any>(null);

    useEffect(() => {
        setShowLegend(stateShowLegend);
    }, [stateShowLegend]);
    
    useEffect(() => {
        setShipPosition(stateSelectShipPosition);
    }, [stateSelectShipPosition]);

    const shouldInit = () => {
        if (!isBoolean(paramsService.showLegend)) return true;
        return paramsService.showLegend;
    }

    useEffect(() => {
        if (isObjectNull(pecExam) || !shouldInit()) return;
        stateSetShowLegend(true);
        paramsService.onShowLegendChange(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pecExam]);

    useEffect(() => {
        
        if (isObjectNull(pilotage) || !shouldInit()) return;
        stateSetShowLegend(true);
        paramsService.onShowLegendChange(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pilotage]);

    useEffect(() => {
        if (isObjectNull(shipPosition) || !shouldInit()) return;
        stateSetShowLegend(true);
        paramsService.onShowLegendChange(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipPosition]);

    return (
        showLegend &&
        <MapControl position={ControlPosition.LEFT_BOTTOM}>
            <div className="google-map-legend google-map-legend-active">
                <div><div onClick={onClick}><X size={16} /></div></div>
                <MapLegendPecExam pecExam={pecExam}/>
                <MapLegendPilotage pilotage={pilotage}/>
                <MapLegendShipPosition pilotage={pilotage} shipPosition={shipPosition} />
            </div>
        </MapControl>
    )

    function onClick() {
        stateSetShowLegend(!showLegend);
    }
}

interface MapLegendPecExamProps {
    pecExam: any
}

const MapLegendPecExam = ({ pecExam }: MapLegendPecExamProps) => {

    const properties = MapPropertiesService.properties;

    const darkmode = useStoreSelector(AppStore, (state) => state.darkmode);

    if (isObjectNull(pecExam)) return;

    return (
        <div className="map-legend-pec-exam font-size-md">
            <div>{parse(pecExam.examName)}</div>
            <div>
                <div
                    className="map-legend-rectangle map-legend-rectangle-granted"
                    style={{
                        borderColor: darkmode ? properties.ColorFairwayArea : properties.ColorFairwayAreaStroke
                    }}>
                    <div
                        style={{
                            backgroundColor: properties.ColorFairwayArea,
                            opacity: 0.6
                        }}/>
                </div>
                <div>Allerede innvilgede omr&aring;der</div>
            </div>
            <div>
                <div
                    className="map-legend-rectangle map-legend-rectangle-applied"
                    style={{
                        borderColor: darkmode ? properties.ColorFairwayAreaApplied : properties.ColorFairwayAreaStroke
                    }}>
                    <div
                        style={{
                            backgroundColor: properties.ColorFairwayAreaApplied,
                            opacity: 0.6
                        }}>
                    </div>
                </div>
                <div>Innvilges etter pr&oslash;ve</div>
            </div>
        </div>
    )
}

interface MapLegendPilotageProps {
    pilotage: any
}

const MapLegendPilotage = ({ pilotage }: MapLegendPilotageProps) => {

    if (isObjectNull(pilotage)) return;

    return (
        <div className="map-legend-pilotage font-size-md">
            <div className="text-truncate">
                Fra: {pilotage.fromLocation.name}
            </div>
            <div className="text-truncate">
                Til: {pilotage.toLocation.name }
            </div>
        </div>
    )
}

interface MapLegendShipPositionProps {
    pilotage: any,
    shipPosition: any,
}

const MapLegendShipPosition = ({ pilotage, shipPosition }: MapLegendShipPositionProps) => {

    const [fromTime, setFromTime] = useState<any | null>(null);
    const [eta, setEta] = useState<any | null>(null);
    const [stampAge, setStampAge] = useState<any | null>(null);
    const [distance, setDistance] = useState<string | null>(null);

    const mapProperties = MapPropertiesService.properties;

    useEffect(() => {
        initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pilotage, shipPosition]);

    return (
        ((!isObjectNull(pilotage) && !isObjectNull(shipPosition))) &&
            <div className="map-legend-ais font-size-xs">
                {
                    (fromTime) ?
                        <div>
                            <div className="map-legend-ais-clock"><div><Clock/></div><div>{fromTime.format('HH:mm')}</div></div>
                            <div>{formatEta(eta)}</div>
                        </div>
                        :
                        (!eta) &&
                            <div>{formatEta(eta)}</div>
                }
                <div>
                    SOG: {shipPosition.speedOverGround?.toFixed(2)} knop
                </div>
                {
                    (stampAge) &&
                        <div>Sist oppdatert: {stampAge.minutes()}min</div>
                }
                {
                    (distance) &&
                        <div>
                            Avstand: {distance} nm
                        </div>
                }
            
            </div>
    );

    function initialize() {
        if (!pilotage || !shipPosition) return;

        let calculatedDistance = 0;

        const pilotageDetail = pilotage.pilotageDetail;
        const fromTime = Moment(pilotageDetail.fromTime);
        const stampAge = Moment.duration(Moment().diff(Moment(shipPosition.timestamp)));

        const isFromLocationAtSea = isLocationAtSea(pilotage.fromLocation);
        const isToLocationAtSea = isLocationAtSea(pilotage.toLocation);

        if (
            (isFromLocationAtSea && !isToLocationAtSea) ||
            (!isFromLocationAtSea && !isToLocationAtSea) ||
            (isFromLocationAtSea && isToLocationAtSea)
        ) {
            if (fromTime > Moment()) {
                calculatedDistance = getDistance(pilotage.fromLocation);

                setFromTime(fromTime);

                const eta = getEta(shipPosition, calculatedDistance); 
                setEta(eta);
            }
        } else if (isToLocationAtSea) {
            calculatedDistance = getDistance(pilotage.toLocation);
            const eta = getEta(shipPosition, calculatedDistance);
            
            if (eta && eta > Moment()) {
                setEta(eta);
            }
        }

        setStampAge(stampAge);

        if (calculatedDistance <= 0) return;
        setDistance(calculatedDistance.toFixed(2));
    }

    function getDistance(startLocation: any):number {
        return calculateDistance(shipPosition.latitude, shipPosition.longitude, startLocation.latitude, startLocation.longitude);
    }
     
    function getEta(shipPosition: any, distance: number) {
        if (shipPosition.speedOverGround < mapProperties.MinimumSpeed || distance <= 0) return null;

        const travelTimeInMinutes = Math.round((distance * 60) / shipPosition.speedOverGround);
        const arrivalTime = Moment().add(travelTimeInMinutes, 'm');
        return arrivalTime;
    }


    function formatEta(eta:any) {
        if (!eta) return "";
        const formattedEta = eta.format('DD.MM HH:mm');
        return `ETA: ${formattedEta}`;
    }
}

